import { cn } from '@/lib/utils'

const SIZES = {
  xs: 'size-5 text-xs',
  sm: 'size-6 text-xs',
  md: 'size-8 text-xs',
  lg: 'size-10 text-sm',
  xl: 'size-14 text-base',
} as const

/** Dutch name particles: "Pieter de Vries" → PV, "Jan Geert van Hall" → JG. */
const PARTICLES = new Set(['de', 'den', 'der', 'van', 'het', 'te', 'ten', 'ter', "'t", 'la', 'le', 'du', 'von'])

/** The letters of a word as whole characters, without apostrophes ("O'Brien" → O B r …). */
function letters(word: string): string[] {
  return Array.from(word.replaceAll("'", ''))
}

/**
 * Two initials for the CRM avatars: the first word plus the next word that is no
 * particle ("Jan Geert van Hall" → JG), the first two letters of a single word, "?" when
 * the name has no letters. Not the `initials` of `@/lib/utils` that the older avatars
 * use: that one takes the first and the last word ("JH") and falls back to "–".
 */
export function initials(name: string): string {
  const words = name
    // Compose "e" + accent into "é", or the accent would count as a word break.
    .normalize('NFC')
    // Typographic apostrophes (U+2018, U+2019, U+02BC) are the plain one: "’t" = "'t".
    .replace(/[\u2018\u2019\u02BC]/g, "'")
    .replace(/[^\p{L}\p{M}\p{N}']+/gu, ' ')
    .split(/\s+/)
    .filter((word) => letters(word).length > 0)
  if (words.length === 0) return '?'
  const [first, ...rest] = words
  const second = rest.find((word) => !PARTICLES.has(word.toLowerCase()))
  const picked = second ? [letters(first)[0], letters(second)[0]] : letters(first).slice(0, 2)
  return picked.join('').toUpperCase()
}

/**
 * Initials avatar for a person (round, brand gradient) or a company (square,
 * neutral) — design §4.0 / DS-10. Decorative: the name is always shown next to it.
 * Named `EntityAvatar` so the Radix `Avatar` in this folder stays untouched.
 */
export function EntityAvatar({
  kind,
  name,
  size = 'md',
  className,
}: {
  kind: 'person' | 'company'
  name: string
  size?: keyof typeof SIZES
  className?: string
}) {
  return (
    <span
      aria-hidden
      data-kind={kind}
      className={cn(
        'inline-flex shrink-0 items-center justify-center font-sans font-bold tracking-[.02em] select-none',
        SIZES[size],
        kind === 'person'
          ? 'rounded-full bg-vonk-avatar text-white shadow-[var(--avatar-shadow)]'
          : 'rounded-md border border-border bg-muted text-foreground',
        className,
      )}
    >
      {initials(name)}
    </span>
  )
}
